/**
 * Encrypted Asset Storage Service
 *
 * Provides secure browser-based storage for quantum-encrypted asset data.
 * Uses IndexedDB for persistent storage with additional encryption layers.
 */

import type { EncryptedData, SignatureData } from '@/types/quantum';
import { LocalStorageFallback } from './StorageFallback';

export interface StoredEncryptedAsset {
  assetId: string;
  safeAddress: string;
  encryptedData: EncryptedData;
  signature: SignatureData;
  metadata: string;
  timestamp: number;
  algorithm: string;
  securityLevel: number;
}

export interface AssetStorageMetadata {
  assetId: string;
  safeAddress: string;
  isEncrypted: boolean;
  encryptionTimestamp: number;
  algorithm: string;
  securityLevel: number;
  hasBackupSignature: boolean;
}

/**
 * IndexedDB-based storage for encrypted assets
 */
export class EncryptedAssetStorage {
  private dbName = 'QuantumSafeVault';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private fallback: LocalStorageFallback;
  private useFallback = false;

  constructor() {
    this.fallback = new LocalStorageFallback();
    // Initialize DB lazily when first needed
  }

  /**
   * Initialize IndexedDB database
   */
  private async initializeDB(): Promise<void> {
    return new Promise((resolve) => {
      console.log('EncryptedAssetStorage: Initializing IndexedDB...', {
        dbName: this.dbName,
        dbVersion: this.dbVersion
      });

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('EncryptedAssetStorage: Failed to open IndexedDB', request.error);
        console.log('EncryptedAssetStorage: Falling back to localStorage');
        this.useFallback = true;
        resolve(); // Don't reject - use fallback
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        console.log('EncryptedAssetStorage: IndexedDB opened successfully', {
          dbName: this.db.name,
          version: this.db.version,
          objectStoreNames: Array.from(this.db.objectStoreNames)
        });
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('EncryptedAssetStorage: Database upgrade needed', {
          oldVersion: event.oldVersion,
          newVersion: event.newVersion
        });

        // Create encrypted assets store
        if (!db.objectStoreNames.contains('encryptedAssets')) {
          const assetStore = db.createObjectStore('encryptedAssets', {
            keyPath: ['safeAddress', 'assetId']
          });
          assetStore.createIndex('safeAddress', 'safeAddress', { unique: false });
          assetStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('EncryptedAssetStorage: Created encryptedAssets store');
        }

        // Create metadata store for quick lookups
        if (!db.objectStoreNames.contains('assetMetadata')) {
          const metadataStore = db.createObjectStore('assetMetadata', {
            keyPath: ['safeAddress', 'assetId']
          });
          metadataStore.createIndex('safeAddress', 'safeAddress', { unique: false });
          console.log('EncryptedAssetStorage: Created assetMetadata store');
        }
      };
    });
  }

  /**
   * Store encrypted asset data
   */
  async storeEncryptedAsset(
    safeAddress: string,
    assetId: string,
    encryptedData: EncryptedData,
    signature: SignatureData,
    metadata: string
  ): Promise<void> {
    await this.ensureDBReady();

    const storedAsset: StoredEncryptedAsset = {
      assetId,
      safeAddress,
      encryptedData,
      signature,
      metadata,
      timestamp: Date.now(),
      algorithm: encryptedData.algorithm,
      securityLevel: encryptedData.securityLevel
    };

    const assetMetadata: AssetStorageMetadata = {
      assetId,
      safeAddress,
      isEncrypted: true,
      encryptionTimestamp: storedAsset.timestamp,
      algorithm: encryptedData.algorithm,
      securityLevel: encryptedData.securityLevel,
      hasBackupSignature: signature.algorithm === 'SLH-DSA'
    };

    console.log('EncryptedAssetStorage: Storing encrypted asset...', {
      safeAddress,
      assetId,
      encryptedDataSize: encryptedData.encryptedData.length,
      signatureSize: signature.signature.length,
      algorithm: encryptedData.algorithm,
      signatureAlgorithm: signature.algorithm,
      usingFallback: this.useFallback
    });

    // Use fallback if IndexedDB failed
    if (this.useFallback) {
      return await this.fallback.storeEncryptedAsset(safeAddress, assetId, storedAsset, assetMetadata);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['encryptedAssets', 'assetMetadata'], 'readwrite');

      transaction.oncomplete = () => {
        console.log('EncryptedAssetStorage: Successfully stored encrypted asset', { safeAddress, assetId });
        resolve();
      };

      transaction.onerror = () => {
        console.error('EncryptedAssetStorage: Failed to store encrypted asset', transaction.error);
        reject(new Error(`Failed to store encrypted asset: ${transaction.error}`));
      };

      // Store the encrypted asset
      const assetStore = transaction.objectStore('encryptedAssets');
      const assetRequest = assetStore.put(storedAsset);

      assetRequest.onerror = () => {
        console.error('EncryptedAssetStorage: Error storing asset data', assetRequest.error);
      };

      // Store the metadata
      const metadataStore = transaction.objectStore('assetMetadata');
      const metadataRequest = metadataStore.put(assetMetadata);

      metadataRequest.onerror = () => {
        console.error('EncryptedAssetStorage: Error storing metadata', metadataRequest.error);
      };
    });
  }

  /**
   * Retrieve encrypted asset data
   */
  async getEncryptedAsset(safeAddress: string, assetId: string): Promise<StoredEncryptedAsset | null> {
    await this.ensureDBReady();

    if (this.useFallback) {
      return await this.fallback.getEncryptedAsset(safeAddress, assetId);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['encryptedAssets'], 'readonly');
      const store = transaction.objectStore('encryptedAssets');
      const request = store.get([safeAddress, assetId]);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve encrypted asset'));
      };
    });
  }

  /**
   * Get all encrypted assets for a Safe
   */
  async getEncryptedAssetsForSafe(safeAddress: string): Promise<AssetStorageMetadata[]> {
    await this.ensureDBReady();

    if (this.useFallback) {
      return await this.fallback.getEncryptedAssetsForSafe(safeAddress);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['assetMetadata'], 'readonly');
      const store = transaction.objectStore('assetMetadata');
      const index = store.index('safeAddress');
      const request = index.getAll(safeAddress);

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve encrypted assets list'));
      };
    });
  }

  /**
   * Remove encrypted asset
   */
  async removeEncryptedAsset(safeAddress: string, assetId: string): Promise<void> {
    await this.ensureDBReady();

    if (this.useFallback) {
      return await this.fallback.removeEncryptedAsset(safeAddress, assetId);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['encryptedAssets', 'assetMetadata'], 'readwrite');

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error('Failed to remove encrypted asset'));

      // Remove the encrypted asset
      const assetStore = transaction.objectStore('encryptedAssets');
      assetStore.delete([safeAddress, assetId]);

      // Remove the metadata
      const metadataStore = transaction.objectStore('assetMetadata');
      metadataStore.delete([safeAddress, assetId]);
    });
  }

  /**
   * Check if an asset is encrypted
   */
  async isAssetEncrypted(safeAddress: string, assetId: string): Promise<boolean> {
    await this.ensureDBReady();

    if (this.useFallback) {
      const metadata = this.fallback.getAllMetadata();
      const assetKey = `${safeAddress}:${assetId}`;
      return !!metadata[assetKey]?.isEncrypted;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['assetMetadata'], 'readonly');
      const store = transaction.objectStore('assetMetadata');
      const request = store.get([safeAddress, assetId]);

      request.onsuccess = () => {
        resolve(!!request.result?.isEncrypted);
      };

      request.onerror = () => {
        reject(new Error('Failed to check asset encryption status'));
      };
    });
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    totalAssets: number;
    totalSize: number;
    safesWithAssets: string[];
  }> {
    await this.ensureDBReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['encryptedAssets'], 'readonly');
      const store = transaction.objectStore('encryptedAssets');
      const request = store.getAll();

      request.onsuccess = () => {
        const assets = request.result || [];
        const safes = new Set(assets.map(asset => asset.safeAddress));
        const totalSize = assets.reduce((size, asset) => {
          return size + asset.encryptedData.encryptedData.length + asset.signature.signature.length;
        }, 0);

        resolve({
          totalAssets: assets.length,
          totalSize,
          safesWithAssets: Array.from(safes)
        });
      };

      request.onerror = () => {
        reject(new Error('Failed to get storage statistics'));
      };
    });
  }

  /**
   * Clear all encrypted assets (for testing/reset)
   */
  async clearAllAssets(): Promise<void> {
    await this.ensureDBReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['encryptedAssets', 'assetMetadata'], 'readwrite');

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error('Failed to clear encrypted assets'));

      transaction.objectStore('encryptedAssets').clear();
      transaction.objectStore('assetMetadata').clear();
    });
  }

  /**
   * Ensure database is ready for operations
   */
  private async ensureDBReady(): Promise<void> {
    if (!this.db) {
      await this.initializeDB();
    }
  }

  /**
   * Debug function to inspect database contents
   */
  async debugDatabaseContents(): Promise<void> {
    await this.ensureDBReady();

    console.log('=== EncryptedAssetStorage DEBUG ===');
    console.log('Using Fallback:', this.useFallback);

    if (this.useFallback) {
      console.log('Storage Method: LocalStorage Fallback');
      this.fallback.debugContents();
      const stats = await this.getStorageStats();
      console.log('Storage Stats:', stats);
    } else {
      console.log('Storage Method: IndexedDB');
      console.log('Database:', {
        name: this.db!.name,
        version: this.db!.version,
        objectStoreNames: Array.from(this.db!.objectStoreNames)
      });

      // Count encrypted assets
      const stats = await this.getStorageStats();
      console.log('Storage Stats:', stats);

      // List all stored assets
      const transaction = this.db!.transaction(['encryptedAssets', 'assetMetadata'], 'readonly');

      const assetsRequest = transaction.objectStore('encryptedAssets').getAll();
      const metadataRequest = transaction.objectStore('assetMetadata').getAll();

      return new Promise((resolve) => {
        transaction.oncomplete = () => {
          console.log('Encrypted Assets:', assetsRequest.result);
          console.log('Asset Metadata:', metadataRequest.result);
          console.log('=== END DEBUG ===');
          resolve();
        };
      });
    }

    console.log('=== END DEBUG ===');
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

/**
 * Create a singleton instance for the application
 */
let storageInstance: EncryptedAssetStorage | null = null;

export function getEncryptedAssetStorage(): EncryptedAssetStorage {
  if (!storageInstance) {
    storageInstance = new EncryptedAssetStorage();
  }
  return storageInstance;
}

/**
 * Debug function to inspect storage - available globally
 */
export async function debugQuantumStorage(): Promise<void> {
  const storage = getEncryptedAssetStorage();
  await storage.debugDatabaseContents();
}

/**
 * Reset function to clear all encrypted assets - available globally
 */
export async function resetQuantumStorage(): Promise<void> {
  const storage = getEncryptedAssetStorage();
  await storage.clearAllAssets();
  console.log('EncryptedAssetStorage: All encrypted assets cleared');
}

// Make it available on window for browser console debugging
if (typeof window !== 'undefined') {
  (window as any).debugQuantumStorage = debugQuantumStorage;
  (window as any).resetQuantumStorage = resetQuantumStorage;
}

export default EncryptedAssetStorage;